/* eslint-disable no-use-before-define */
import {Record as record, List} from 'immutable';

import {unique} from '../../helpers/Immutable';
import {construct, resolveAll, fields} from '../../helpers/LinkMapper';
import {getResolvers, getDefaults} from '../../helpers/Records';
import Fare from '../Fare';
import Service from '../Service';
import OverallCheapestSelection from '../OverallCheapestSelection';
import CheapestFares from '../CheapestFares';

const schema = {
    // services
    outwardServices: [resolveAll.as(Service)],
    inwardServices: [resolveAll.as(Service)],

    // singles
    outwardSingles: [resolveAll.as(Fare)],
    inwardSingles: [resolveAll.as(Fare)],
    totalSinglePrice: [fields.number],

    // returns
    returns: [resolveAll.as(Fare)],
    totalReturnPrice: [fields.number],
};

/**
 * @callback fareFilterCallback
 * @param {Fare} fare - a candidate Fare to test against criteria for the journey
 * @returns {Boolean} true if the fare is eligible according to the criteria
 */

export default class CheapestMatchingFares extends record(getDefaults(schema)) {
    static fromJSON(json = {}) {
        return construct(CheapestMatchingFares, json, getResolvers(schema));
    }

    /**
     * Finds the cheapest fares in the given outward/inward services that match an _optional_
     * fare filer
     * It is possible that the cheapest "singles" (out+in) are the same price as the cheapest
     * "return", in which case, return both, otherwise only return the cheapest fare type
     *
     * @param {Service[]} outwardServices - list of services forming the outward part of the journey
     * @param {Service[]} [inwardServices] - optional list of services forming the return part of the journey
     * @param {fareFilterCallback} [fareFilter] - an optional filter to create a subset of fares to check against
     * @returns {CheapestMatchingFares}
     */
    static fromServices(outwardServices, inwardServices, fareFilter) {
        const getFarePrice = fares => fares && fares.first().totalPrice;

        let cheapest = new CheapestMatchingFares({outwardServices, inwardServices});

        // Find the cheapest fares in each fare type
        const {outwardSingles, inwardSingles, returns} = getCheapestFares(
            outwardServices,
            inwardServices,
            fareFilter,
        );

        // return only outward singles if it's just an outward journey
        if (!inwardServices || inwardServices.isEmpty()) {
            return cheapest.merge({
                outwardSingles: outwardSingles,
                totalSinglePrice: getFarePrice(outwardSingles),
            });
        }

        // Add singles *only* if we have singles in both directions
        let cheapestSingles = cheapest;
        if (outwardSingles && inwardSingles) {
            cheapestSingles = cheapestSingles.merge({
                outwardSingles,
                inwardSingles,
                totalSinglePrice: getFarePrice(outwardSingles) + getFarePrice(inwardSingles),
            });
        }

        if (returns) {
            // Temporary record for returns
            let cheapestReturns = cheapest.merge({
                returns,
                totalReturnPrice: getFarePrice(returns),
            });
            if (cheapestReturns.totalReturnPrice < cheapestSingles.totalSinglePrice) {
                // filter out singles if they are more expensive
                cheapest = cheapestReturns;
            } else if (cheapestReturns.totalReturnPrice === cheapestSingles.totalSinglePrice) {
                // merge returns with singles if they are the same price
                cheapest = cheapestSingles.merge({
                    returns: cheapestReturns.returns,
                    totalReturnPrice: cheapestReturns.totalReturnPrice,
                });
            } else {
                cheapest = cheapestSingles;
            }
        } else {
            cheapest = cheapestSingles;
        }

        return cheapest;
    }

    hasFare() {
        return Boolean(this.getPrice());
    }

    getPrice() {
        return this.totalReturnPrice || this.totalSinglePrice;
    }

    /**
     * @returns {OverallCheapestSelection} the *fastest* matching fare as an `OverallCheapestSelection`
     */
    getOverallCheapest() {
        const getDuration = service => service.getTripDuration();
        const serviceHasFare = fare => service => service.isValidWithFare(fare);

        // Sort the services - fastest first
        const outwardServices = this.outwardServices && this.outwardServices.sortBy(getDuration);
        const inwardServices = this.inwardServices ? this.inwardServices.sortBy(getDuration) : new List();

        // A helper to find the fastest Service that is valid for the given Fare
        const getFastestService = (fare, sortedServices) =>
            sortedServices.find(serviceHasFare(fare));

        /**
         * Given:
         * * "single" tickets are valid in only one direction
         * * there could be *multiple* singles which are the same price
         * * they might not be valid on the same service
         * ... we look through each fare, and find the fastest service for that fare
         * and then we sort them (fastest first), and pull out the fastest one
         *
         * @param {Fare[]} fares
         * @param {Service[]} fromServices - A sorted list of services
         * @returns {Array} Array containing the duration (in millis), the fare, and the service
         */
        const getFastestSingle = (fares, fromServices) =>
            fares &&
            !fares.isEmpty() &&
            fares
                .map(fare => {
                    const service = getFastestService(fare, fromServices);
                    return service && [getDuration(service).asMilliseconds(), fare, service];
                })
                .filter(Boolean)
                .sortBy(([journeyTime]) => journeyTime)
                .first();

        /**
         * Given:
         * * "return" tickets are valid in **BOTH** directions
         * * there could be *multiple* returns which are the same price
         * * they might not be valid on the same service
         * ... we look through each fare, and find the fastest outward & inward services for that fare
         * and then we sort them by the **combined** duration (fastest first), and pull out the fastest one
         *
         * @param {Fare[]} fares
         * @param {Service[]} fromServices - A sorted list of services
         * @returns {Array} Array containing the duration (in millis), the fare, and the service
         */
        const getFastestReturn = fares =>
            fares &&
            !fares.isEmpty() &&
            fares
                .map(fare => {
                    const outService = getFastestService(fare, this.outwardServices);
                    const inService = getFastestService(fare, this.inwardServices);
                    const duration =
                        outService &&
                        inService &&
                        getDuration(outService).add(getDuration(inService));
                    return duration && [duration.asMilliseconds(), fare, outService, inService];
                })
                .filter(Boolean)
                .sortBy(([journeyTime]) => journeyTime)
                .first();

        const [outwardSingleTime, outwardSingleFare, outwardSingleService] =
            getFastestSingle(this.outwardSingles, outwardServices) || [];

        const [returnSingleTime, inboundSingleFare, inwardSingleService] =
            getFastestSingle(this.inwardSingles, inwardServices) || [];

        const [returnTime, returnFare, outwardService, inboundService] =
            getFastestReturn(this.returns, outwardServices, inwardServices) || [];

        const singleTime = (outwardSingleTime || 0) + (returnSingleTime || 0);

        if (singleTime && (!returnTime || singleTime <= returnTime)) {
            // the overall cheapest/fastest fare are singles
            return new OverallCheapestSelection({
                outwardService: outwardSingleService,
                inboundService: inwardSingleService,
                cheapestFares: new CheapestFares({
                    outwardSingle: outwardSingleFare,
                    inboundSingle: inboundSingleFare,
                    totalPrice:
                        outwardSingleFare.totalPrice +
                        (inboundSingleFare ? inboundSingleFare.totalPrice : 0),
                }),
            });
        } else if (returnTime && (!singleTime || returnTime <= singleTime)) {
            // the overall cheapest/fastest fare is a return fare
            return new OverallCheapestSelection({
                outwardService,
                inboundService,
                cheapestFares: new CheapestFares({
                    return: returnFare,
                    totalPrice: returnFare.totalPrice,
                }),
            });
        }
        return new OverallCheapestSelection();
    }
}

/**
 * Find the cheapest fares in the given services that match the optional `fareFilter`
 * Each fare type returned will be the _same_ lowest price, but the combined outward+inward singles
 * price may differ from the return price. We will reduce to the cheapest journey fare later on
 *
 * @param {Service[]} outwardServices - list of services forming the outward part of the journey
 * @param {Service[]} [inwardServices] - optional list of services forming the return part of the journey
 * @param {fareFilterCallback} [fareFilter] - an optional filter to create a subset of fares to check against
 * @returns {Object} cheapest
 * @returns {Fare[]} cheapest.outwardSingles
 * @returns {Fare[]} cheapest.inwardSingles
 * @returns {Fare[]} cheapest.returns
 */
function getCheapestFares(outwardServices, inwardServices, fareFilter = fare => fare) {
    const getFares = type => service => service.fares.get(type);
    const getId = fare => fare.id;
    const getPrice = fare => fare.totalPrice;
    const isValidReturn = fare => inwardServices.some(service => service.isValidWithFare(fare));

    const getCheapest = (services, fareGetter, filter = fare => fare) => {
        // Find the matching fares
        const matchingFares = services
            .flatMap(fareGetter)
            .filter(fare => fareFilter(fare) && filter(fare));
        // de-dupe, group by price and then return all the fares at the cheapest price
        return unique(matchingFares, getId)
            .sortBy(getPrice)
            .groupBy(getPrice)
            .first();
    };

    // Get the cheapest fares
    const outwardSingles = outwardServices && getCheapest(outwardServices, getFares('singles'));
    const inwardSingles = inwardServices && getCheapest(inwardServices, getFares('singles'));
    const returns =
        inwardServices &&
        outwardServices &&
        getCheapest(outwardServices, getFares('returns'), isValidReturn);

    return {outwardSingles, inwardSingles, returns};
}



// WEBPACK FOOTER //
// ./src/records/trip/CheapestMatchingFares.js